<script>
	import CustomButton from "src/components/CustomButton/CustomButton.svelte";
	import CustomCollapseCard from "src/components/CustomCollapseCard/CustomCollapseCard.svelte";
	import CustomFileInput from "src/components/CustomFileInput/CustomFileInput.svelte";
	import ErrorMessageHandler from "src/components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "src/components/PageHeader/PageHeader.svelte";
	import DamageCarousel from "src/components/ReportCard/DamageCarousel.svelte";
	import { navigate } from "svelte-routing";
	import { Col, Row } from "sveltestrap";
	import { onMount } from "svelte";
	import client from "../../../../services/client";
	import {
		convertDateOnly,
		convertTimeOnly,
	} from "../../../../services/helpers";
	import { loading } from "src/store/store";
	import {
		extractErrorMessage,
		scrollToTop,
		splitCamelCaseAndCapitalize,
	} from "src/utils/misc";
	import QuotationDamageInput from "./QuotationDamageInput.svelte";
	import QuotationDamageDisplay from "./QuotationDamageDisplay.svelte";
	import LoaderContainer from "src/components/Loader/LoaderContainer.svelte";
	import ConfirmModal from "src/components/CustomModal/ConfirmModal.svelte";

	export let id;
	let caseDetails = [];
	let vehicleId;
	let vehicleDetails = [];
	let err;
	let selectedSides = [];
	let finalAmount = null;
	let remarks = "";
	let isOpen = false;

	//create a callback functino to change the values of selectedSides, finalAmount, and remarks
	function updateValues({ sides, amount, remark }) {
		// persist the values to the parent component
		if (sides !== undefined) selectedSides = sides;
		if (amount !== undefined) finalAmount = amount;
		if (remark !== undefined) remarks = remark;
	}

	// stores new invoices/photos
	let newWorkshopInvoice = [];
	let newAdditionalPhotos = [];

	// combined arrays mixing URLs and File objects
	$: workshopInvoice = [
		...(caseDetails.workshopInvoices || []),
		...newWorkshopInvoice,
	];
	$: additionalPhotos = [
		...(caseDetails.additionalPhotos || []),
		...newAdditionalPhotos,
	];

	$: damageLocations = caseDetails.finalDamagedSides
		? caseDetails.finalDamagedSides
				.map((d) => d.side)
				.join(", ")
				.replace(/([a-z])([A-Z])/g, "$1 $2")
		: "";

	// only allow creation if there's new files added
	$: disableSubmit =
		newWorkshopInvoice.length === 0 && newAdditionalPhotos.length === 0;

	// remove a file from either backend list or new uploads
	function removeWorkshopFile(idx) {
		// if idx is within the existing workshopInvoices, remove from that
		if (idx < (caseDetails.workshopInvoices || []).length) {
			caseDetails.workshopInvoices.splice(idx, 1);
			caseDetails = { ...caseDetails };
		} else {
			newWorkshopInvoice.splice(
				idx - (caseDetails.workshopInvoices || []).length,
				1
			);
			newWorkshopInvoice = [...newWorkshopInvoice];
		}
	}

	function removePhotoFile(idx) {
		if (idx < (caseDetails.additionalPhotos || []).length) {
			caseDetails.additionalPhotos.splice(idx, 1);
			caseDetails = { ...caseDetails };
		} else {
			newAdditionalPhotos.splice(
				idx - (caseDetails.additionalPhotos || []).length,
				1
			);
			newAdditionalPhotos = [...newAdditionalPhotos];
		}
	}

	function handleFileChange(e) {
		const files = Array.from(e.target.files);
		if (e.target.name === "workshopInvoice") {
			// append selected files to newWorkshopInvoice list
			newWorkshopInvoice = [...newWorkshopInvoice, ...files];
		} else {
			newAdditionalPhotos = [...newAdditionalPhotos, ...files];
		}
	}

	async function fetchCaseDetails(id) {
		try {
			loading.set(true);
			const response = await client(`/v1/CaseManagement/case/${id}`, {
				method: "GET",
				version: "Tasking",
			});
			caseDetails = response.data;
			vehicleId = caseDetails.vehicleId;
			fetchVehicleDetails(vehicleId);
			loading.set(false);
		} catch (error) {
			err = error.message || "Failed to fetch case details.";
			console.error(err);
			loading.set(false);
		}
	}

	async function fetchVehicleDetails(vehicleId) {
		try {
			loading.set(true);
			const response = await client(`/v1/${vehicleId}/details`, {
				method: "GET",
				version: "Vehicle",
			});
			vehicleDetails = response.data;
			loading.set(false);
		} catch (error) {
			err = error.message || "Failed to fetch vehicle details.";
			console.error(err);
			loading.set(false);
		}
	}

	onMount(() => {
		fetchCaseDetails(id);
	});

	async function handleSubmit() {
		try {
			const data = new FormData();
			console.log("Selected sides:", selectedSides);

			if (caseDetails.status === "ToQuote") {
				selectedSides.forEach((val, i) => {
					data.append(`finalDamagedSides[${i}].side`, val);
				});
				data.append("finalAmount", finalAmount);
				data.append("remarks", remarks);
			} else if (caseDetails.status === "ToBill") {
				caseDetails.finalDamagedSides.forEach((val, i) => {
					data.append(`finalDamagedSides[${i}].side`, val.side);
				});
			}

			// Append workshopInvoice files/URLs
			workshopInvoice.forEach((file, idx) => {
				if (file instanceof File) {
					data.append("workshopInvoice", file);
				} else {
					// For URLs, send as a separate field (backend must support this)
					data.append("workshopInvoiceUrls", file);
				}
			});

			// Append additionalPhotos files/URLs
			additionalPhotos.forEach((file, idx) => {
				if (file instanceof File) {
					data.append("additionalPhotos", file);
				} else {
					data.append("additionalPhotosUrls", file);
				}
			});

			let response;
			if (caseDetails.status === "ToQuote") {
				response = await client(
					`/v1/CaseManagement/case/${id}/quotation`,
					{
						method: "POST",
						version: "Tasking",
						data,
						header: "multipart",
					}
				);
			} else if (caseDetails.status === "ToBill") {
				response = await client(
					`/v1/caseManagement/case/${id}/upload`,
					{
						method: "POST",
						version: "Tasking",
						data,
						header: "multipart",
					}
				);
			}
			if (response.status === 204) {
				isOpen = false;
				navigate("/issues/case-management?tab=3");
			}
		} catch (error) {
			// if (error.response.data.code === 29006) {
			// 	err = `Error ${error.response.data.code}: ${error.response.data.message}`;
			// } else {
			// 	err = error.message || "Failed to create quotation.";
			// }
			extractErrorMessage(error);
			console.log(error);
			scrollToTop();
		}
	}

	const quotationDamage = {
		ToQuote: QuotationDamageInput,
		ToBill: QuotationDamageDisplay,
	};

	$: toQuoteDisabled =
		remarks.trim() === "" ||
		finalAmount === null ||
		selectedSides.length === 0;

	let headerTexts = {
		ToQuote: "Create Quotation",
		ToBill: "Save Changes",
	};

	let bodyTexts = {
		ToQuote:
			"You are about to create a quotation for this case. Once confirmed, you can only add new workshop invoices or photos. No changes can be made. Are you sure you want to proceed?",
		ToBill: "You are about to save the changes on this quote. Are you sure you want to proceed??",
	};
</script>

<LoaderContainer>
	<Row>
		<Col>
			<div>
				<ErrorMessageHandler message={err} />
				<div class="d-flex align-items-center mb-3">
					<PageHeader title={caseDetails.caseNo} />
					<span class="status-bar"
						>{splitCamelCaseAndCapitalize(
							caseDetails.status === "ToQuote"
								? "Quoting"
								: caseDetails.status
						)}</span
					>
				</div>
			</div>
		</Col>
		<div class="mb-4">
			<CustomCollapseCard
				isOpenDefault={true}
				title="Vehicle Information"
			>
				<div class="container p-0">
					<Row>
						<Col>
							<div class="description">Vehicle Number</div>
							<div class="value">{caseDetails.plateNo}</div>
						</Col>
						<Col>
							<div class="description">Assets Owner</div>
							<div class="value">
								{vehicleDetails.assetOwnerName}
							</div>
						</Col>
					</Row>

					<hr class="divider" />

					<Row>
						<Col>
							<div class="description">Make</div>
							<div class="value">
								{vehicleDetails.vehicleMakeName}
							</div>
						</Col>
						<Col>
							<div class="description">Model</div>
							<div class="value">
								{vehicleDetails.vehicleModelName}
							</div>
						</Col>
					</Row>
				</div>
			</CustomCollapseCard>
		</div>
		<div class="mb-4">
			<CustomCollapseCard
				isOpenDefault={true}
				title="Investigator Information"
			>
				<div class="container p-0">
					<Row>
						<Col>
							<div class="description">Investigator Name</div>
							<div class="value">
								{caseDetails.investigatorName}
							</div>
						</Col>
						<Col>
							<div class="description">Case Created Date</div>
							<div class="value">
								{convertDateOnly(caseDetails.caseCreatedDate)}
								{convertTimeOnly(caseDetails.caseCreatedDate)}
							</div>
						</Col>
					</Row>
				</div>
			</CustomCollapseCard>
		</div>
		<div class="mb-4">
			<CustomCollapseCard isOpenDefault={true} title="Damage Information">
				<div class="container p-0">
					<Row>
						<Col>
							<div class="description">Booking ID</div>
							<div class="value">{caseDetails.bookingNo}</div>
						</Col>
						<Col>
							<div class="description">Start Date & Time</div>
							<div class="value">
								{convertDateOnly(caseDetails.bookingStartTime)}
								{convertTimeOnly(caseDetails.bookingStartTime)}
							</div>
						</Col>
						<Col>
							<div class="description">End Date & Time</div>
							<div class="value">
								{convertDateOnly(caseDetails.bookingEndTime)}
								{convertTimeOnly(caseDetails.bookingEndTime)}
							</div>
						</Col>
					</Row>
					<hr class="divider" />
					<Row>
						<Col>
							<DamageCarousel
								data={{ images: caseDetails.bookingImages }}
								lightbox={true}
							/>
						</Col>
					</Row>
				</div>
			</CustomCollapseCard>
		</div>
		<div class="mb-4">
			<CustomCollapseCard isOpenDefault={true} title="Quotation">
				<svelte:component
					this={quotationDamage[caseDetails.status]}
					{caseDetails}
					{damageLocations}
					{selectedSides}
					{finalAmount}
					{remarks}
					{updateValues}
				/>
			</CustomCollapseCard>
		</div>
		<div>
			<CustomCollapseCard isOpenDefault={true} title="Other">
				<CustomFileInput
					title="Workshop Quotation"
					fileType="file"
					files={workshopInvoice}
					{handleFileChange}
					name="workshopInvoice"
					on:remove={({ detail }) => {
						if (caseDetails.status === "ToQuote") {
							removeWorkshopFile(detail.index);
						}
					}}
				/>

				<CustomFileInput
					title="Additional Photos"
					fileType="image"
					files={additionalPhotos}
					{handleFileChange}
					name="additionalPhotos"
					on:remove={({ detail }) => {
						if (caseDetails.status === "ToQuote") {
							removePhotoFile(detail.index);
						}
					}}
				/>
			</CustomCollapseCard>
		</div>
		<div class="d-flex justify-content-end my-3">
			<div class="me-3">
				<CustomButton
					buttonText="Cancel"
					onClick={() => window.history.back()}
				/>
			</div>
			<div>
				<CustomButton
					buttonText={`${
						caseDetails.status === "ToQuote"
							? "Create"
							: "Save Changes"
					}`}
					color="primary"
					disabled={(caseDetails.status === "ToQuote" &&
						toQuoteDisabled) ||
						(caseDetails.status === "ToBill" && disableSubmit)}
					onClick={() => {
						isOpen = true;
					}}
				/>
			</div>
		</div>
	</Row>
</LoaderContainer>
<ConfirmModal
	open={isOpen}
	toggle={() => (isOpen = false)}
	header={headerTexts[caseDetails.status]}
	bodyText={bodyTexts[caseDetails.status]}
	buttonColor="primary"
	buttonText="Confirm"
	outline={false}
	{handleSubmit}
/>

<style>
	.status-bar {
		background: #ffca3d;
		color: #222;
	}
</style>
